import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TurnDirection } from '@app/features/documents/document-orientation';
import { Orientation } from '@app/features/documents/document.model';
import { PdfFile, PdfPage, PdfRenderJob } from '@app/features/documents/pdf.service';
import { LucideRotateCcw, LucideRotateCw } from '@lucide/angular';

/**
 * Nombre maximal de pixels du dessin d'une page : au-delà, la page agrandie s'affiche un peu
 * moins nette plutôt que d'épuiser la mémoire du navigateur, page après page.
 */
export const maxCanvasPixels = 4096 * 4096;

/**
 * Marge, en part de la zone visible, au-dessus et au-dessous de celle-ci : une page qui s'y trouve
 * est dessinée d'avance pour qu'elle soit prête quand on y arrive en défilant.
 */
const nearMargin = '100% 0px';

/**
 * Une page d'un PDF, dessinée par l'application dans son orientation retenue. Elle porte ses
 * propres commandes de rotation : quand plusieurs pages sont visibles, on sait laquelle on
 * tourne (décision 04 du cadrage 2026-018).
 *
 * Seule une page visible ou voisine est dessinée : une page éloignée libère son dessin et ne
 * garde que ses dimensions, pour que le défilement reste juste. Un long PDF agrandi ne retient
 * ainsi que quelques dessins à la fois.
 */
@Component({
  selector: 'app-pdf-page-view',
  imports: [MatButtonModule, MatTooltipModule, LucideRotateCw, LucideRotateCcw],
  templateUrl: './pdf-page-view.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pdf-page-view.scss'],
})
export class PdfPageView {
  readonly file = input.required<PdfFile>();
  readonly pageNumber = input.required<number>();
  readonly pageCount = input.required<number>();
  readonly rotation = input.required<Orientation>();
  /** Facteur d'agrandissement ; à 1, la page occupe la largeur disponible. */
  readonly zoom = input.required<number>();
  /** Largeur disponible pour la page, en pixels CSS. */
  readonly availableWidth = input.required<number>();
  readonly rotatable = input.required<boolean>();
  /** Zone qui fait défiler la page ; à défaut, la fenêtre. */
  readonly scrollRoot = input<HTMLElement | null>(null);
  readonly turned = output<TurnDirection>();
  /** Émis quand l'utilisateur demande le document d'origine, faute de pouvoir lire la page. */
  readonly openOriginal = output<void>();

  /** La page n'a pas pu être lue ou dessinée. */
  readonly failed = signal(false);
  private readonly attempt = signal(0);
  private readonly page = signal<PdfPage | null>(null);
  /** La page est visible, ou assez proche pour être dessinée d'avance. */
  private readonly near = signal(false);
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
  private renderJob?: PdfRenderJob;

  constructor() {
    effect(() => {
      const file = this.file();
      const pageNumber = this.pageNumber();
      const attempt = this.attempt();
      const current = () =>
        this.file() === file && this.pageNumber() === pageNumber && this.attempt() === attempt;
      untracked(() => {
        this.page.set(null);
        this.failed.set(false);
      });
      file.getPage(pageNumber).then(
        (page) => {
          if (current()) {
            this.page.set(page);
          }
        },
        (error: unknown) => {
          console.error(`Page ${pageNumber} illisible`, error);
          if (current()) {
            this.failed.set(true);
          }
        },
      );
    });

    effect((onCleanup) => {
      const observer = new IntersectionObserver(
        (entries) => this.near.set(entries[entries.length - 1].isIntersecting),
        { root: this.scrollRoot(), rootMargin: nearMargin },
      );
      observer.observe(this.host);
      onCleanup(() => observer.disconnect());
    });

    effect(() => {
      const page = this.page();
      const canvas = this.canvas().nativeElement;
      const rotation = this.rotation();
      const zoom = this.zoom();
      const availableWidth = this.availableWidth();
      const near = this.near();
      if (page && availableWidth > 0) {
        untracked(() => {
          const scale = this.size(page, canvas, rotation, zoom, availableWidth);
          if (near) {
            this.render(page, canvas, scale, rotation);
          } else {
            this.release(page, canvas);
          }
        });
      }
    });

    inject(DestroyRef).onDestroy(() => this.renderJob?.cancel());
  }

  /** Relit la page puis la redessine. */
  retry(): void {
    this.attempt.update((attempt) => attempt + 1);
  }

  /**
   * Donne à la page ses dimensions affichées, dessinée ou non, et renvoie l'échelle de son
   * dessin.
   */
  private size(
    page: PdfPage,
    canvas: HTMLCanvasElement,
    rotation: Orientation,
    zoom: number,
    availableWidth: number,
  ): number {
    const size = page.size(rotation);
    const cssScale = (availableWidth / size.width) * zoom;
    const pixelRatio = window.devicePixelRatio || 1;
    const maxScale = Math.sqrt(maxCanvasPixels / (size.width * size.height));
    canvas.style.width = `${Math.round(size.width * cssScale)}px`;
    canvas.style.height = `${Math.round(size.height * cssScale)}px`;
    return Math.min(cssScale * pixelRatio, maxScale);
  }

  /** Abandonne le dessin d'une page éloignée et rend sa mémoire, sans toucher à ses dimensions. */
  private release(page: PdfPage, canvas: HTMLCanvasElement): void {
    if (this.renderJob) {
      this.renderJob.cancel();
      this.renderJob = undefined;
    }
    if (canvas.width === 0 && canvas.height === 0) {
      return;
    }
    canvas.width = 0;
    canvas.height = 0;
    page.release();
  }

  private render(page: PdfPage, canvas: HTMLCanvasElement, scale: number, rotation: Orientation) {
    this.renderJob?.cancel();
    const job = page.render(canvas, scale, rotation);
    this.renderJob = job;
    job.promise.catch((error: { name?: string }) => {
      // Un dessin abandonné pour un autre — zoom, rotation — n'est pas un échec.
      if (error?.name === 'RenderingCancelledException') {
        return;
      }
      console.error(`Page ${this.pageNumber()} non dessinée`, error);
      if (this.renderJob === job) {
        this.failed.set(true);
      }
    });
  }
}
